import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppDispatch } from "@/redux/hooks";

import { useLazyGetContactProspectDetailQuery } from "../../../redux/contact/contact.api";
import { openContactLeadPanel, setLeadPanelDetail } from "../../../redux/contact/contact.slice";

/**
 * Deep link support for the Contacts overview: a `?prospectId=<id>` query param
 * (used by the Chrome extension's "Open in Signalist" action) auto-opens the lead
 * panel for that prospect, then strips the param so a refresh / panel-close doesn't
 * reopen it. The detail is fetched on demand; if the prospect isn't accessible to
 * the company the panel simply stays closed.
 */
export const useContactLeadPanelDeepLink = (): void => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [triggerGetContactProspectDetail] = useLazyGetContactProspectDetailQuery();
	const handledRef = useRef(false);

	useEffect(() => {
		if (handledRef.current) {
			return;
		}

		const prospectId = Number(searchParams.get("prospectId"));

		if (!prospectId) {
			return;
		}

		handledRef.current = true;

		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);

				next.delete("prospectId");

				return next;
			},
			{ replace: true }
		);

		void (async () => {
			try {
				const detail = await triggerGetContactProspectDetail(prospectId).unwrap();

				dispatch(openContactLeadPanel(detail));
				dispatch(setLeadPanelDetail(detail));
			} catch {
				// Prospect not accessible to this company — leave the panel closed.
			}
		})();
	}, [searchParams, setSearchParams, triggerGetContactProspectDetail, dispatch]);
};
